import { memo } from 'react'
import { cn } from '@/lib/utils'
import { splitTextByKeywords } from '../utils/highlightUtils'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Types
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface HighlightedTextProps {
  text: string
  keywords: string[]
  className?: string
  highlightClassName?: string
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// HighlightedText Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Component that renders text with highlighted keywords
 * - Highlights are case-insensitive
 * - Supports multiple keywords
 * - Accessible with proper contrast in light/dark modes (white text in dark mode)
 * - Memoized for performance
 */
export const HighlightedText = memo(function HighlightedText({
  text,
  keywords,
  className,
  highlightClassName = 'bg-yellow-200 dark:bg-yellow-900/50 dark:text-white font-semibold px-0.5 rounded',
}: HighlightedTextProps) {
  const segments = splitTextByKeywords(text, keywords)

  return (
    <span className={cn('', className)}>
      {segments.map((segment, index) => {
        if (segment.isHighlight) {
          return (
            <mark key={`${segment.text}-${index}`} className={cn(highlightClassName)}>
              {segment.text}
            </mark>
          )
        }
        return <span key={`${segment.text}-${index}`}>{segment.text}</span>
      })}
    </span>
  )
})
